import { useQuery, useMutation } from "convex/react";
import { api } from "convex/_generated/api";
import { Bell, ToggleLeft, ToggleRight, Mail } from "lucide-react";

type ToggleKey =
  | "eventPending"
  | "eventAutoApproved"
  | "venueClaim"
  | "organizerRequest"
  | "verificationRequest";

const TOGGLES: { key: ToggleKey; label: string; description: string }[] = [
  {
    key: "eventPending",
    label: "Events awaiting approval",
    description:
      "Someone submits an event that needs review before it goes on the calendar.",
  },
  {
    key: "eventAutoApproved",
    label: "Auto-approved events",
    description:
      "A trusted organizer or studio publishes an event immediately. No action needed.",
  },
  {
    key: "venueClaim",
    label: "Venue claims",
    description:
      "A studio account claims a venue, or creates one it will own. Needs verification.",
  },
  {
    key: "organizerRequest",
    label: "Organizer access requests",
    description:
      "An organizer asks for permission to post events at a specific venue.",
  },
  {
    key: "verificationRequest",
    label: "Account verification requests",
    description:
      "An organizer or studio asks to be verified so their events publish without review.",
  },
];

export function NotificationSettings() {
  const settings = useQuery(api.notifications.getSettings);
  const updateSettings = useMutation(api.notifications.updateSettings);

  const handleToggle = async (key: ToggleKey, current: boolean) => {
    await updateSettings({ [key]: !current });
  };

  return (
    <section>
      <h2 className="font-semibold text-lg text-foreground mb-1 flex items-center gap-2">
        <Bell className="w-5 h-5" />
        Email Notifications
      </h2>
      <p className="text-sm text-muted-foreground mb-4">
        Choose which activity emails the moderation team. Each notification goes
        to the moderators who can act on it — volunteers work the event queue,
        so claims and access requests go to moderators and admins only.
      </p>

      {settings === undefined ? (
        <div className="space-y-2">
          {TOGGLES.map((t) => (
            <div key={t.key} className="h-20 rounded-lg bg-muted animate-pulse" />
          ))}
        </div>
      ) : (
        <>
          <div className="flex items-start gap-2 text-sm text-muted-foreground mb-4 px-4 py-3 rounded-lg bg-muted">
            <Mail className="w-4 h-4 shrink-0 mt-0.5" />
            {settings.fallbackRecipient ? (
              <span>
                When a notification has no eligible moderator, it falls back to{" "}
                <span className="font-medium text-foreground">
                  {settings.fallbackRecipient}
                </span>
                .
              </span>
            ) : (
              <span>
                No fallback configured — set{" "}
                <code className="font-mono text-xs">ADMIN_EMAIL</code> on the
                Convex deployment, or a notification with no eligible moderator
                is delivered to nobody.
              </span>
            )}
          </div>

          <ul className="flex flex-col gap-2">
            {TOGGLES.map(({ key, label, description }) => {
              const enabled = settings[key];
              const recipients = settings.recipients[key];
              return (
                <li
                  key={key}
                  className="flex items-start justify-between gap-4 p-4 rounded-lg border border-border bg-card"
                >
                  <div className="min-w-0">
                    <p className="font-medium text-foreground">{label}</p>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      {description}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1.5">
                      {recipients.length > 0 ? (
                        <>
                          Goes to{" "}
                          <span className="text-foreground">
                            {recipients.join(", ")}
                          </span>
                        </>
                      ) : (
                        <>
                          No eligible moderator — falls back to{" "}
                          <span className="text-foreground">
                            {settings.fallbackRecipient ?? "nobody"}
                          </span>
                        </>
                      )}
                    </p>
                  </div>
                  <button
                    onClick={() => handleToggle(key, enabled)}
                    className="shrink-0 flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                    title={`${label}: ${enabled ? "on" : "off"}`}
                    aria-pressed={enabled}
                  >
                    {enabled ? (
                      <ToggleRight className="w-6 h-6 text-primary" />
                    ) : (
                      <ToggleLeft className="w-6 h-6 text-muted-foreground" />
                    )}
                    <span className="w-6 text-left">{enabled ? "On" : "Off"}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </section>
  );
}
